import React, { useEffect, useMemo, useCallback } from 'react';
import { useToggle } from 'components/shared/hooks';
import Modal from 'components/shared/modal';
import { EVENTS } from 'constants/config';
import { setQueryStringForCurrentUrl } from 'utils/url';
import { ButtonGroup, Button } from 'ui';
import iconSrc from 'images/publish-success-icon.svg';
import usePermissions from 'hooks/usePermissions';

const PUBLISH_SUCCESS_DISPLAY_PREFERENCE_KEY =
  'display_lesson_published_success';

const PUBLISH_SUCCESS_URL_PARAM = 'publish_success';

const DraftPublishSuccess = () => {
  const { isPermitted: canAssign } = usePermissions({
    type: 'assignment',
    action: 'create',
  });
  const [successMessageVisible, toggleSuccessMessageVisible] = useToggle();

  const displayMessagePreference = useMemo(
    () => localStorage.getItem(PUBLISH_SUCCESS_DISPLAY_PREFERENCE_KEY),
    [],
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (
      urlParams.get(PUBLISH_SUCCESS_URL_PARAM) === 'true' &&
      displayMessagePreference !== 'false'
    ) {
      toggleSuccessMessageVisible();
      urlParams.delete(PUBLISH_SUCCESS_URL_PARAM);
      setQueryStringForCurrentUrl(urlParams.toString());
    }
  }, [displayMessagePreference, toggleSuccessMessageVisible]);

  const toggleDisplayMessagePreference = useCallback(e => {
    if (e.target.checked) {
      localStorage.setItem(PUBLISH_SUCCESS_DISPLAY_PREFERENCE_KEY, false);
    } else {
      localStorage.removeItem(PUBLISH_SUCCESS_DISPLAY_PREFERENCE_KEY);
    }
  }, []);

  const handleAssign = useCallback(
    e => {
      toggleSuccessMessageVisible();
      pubsub.publish(EVENTS.ASSIGN_MODAL_TOGGLE);
    },
    [toggleSuccessMessageVisible],
  );

  return (
    <Modal
      dismissCallback={toggleSuccessMessageVisible}
      show={successMessageVisible}
      title={
        <h1 className="ff-ss">
          {t('drafts.publishing.lesson_success_modal.heading')}
        </h1>
      }
    >
      <div className="fz-16">
        <div className="ta-c mb-32">
          <img
            className="w-180 mr-32"
            src={iconSrc}
            alt={t('common.success')}
          />
        </div>
        <p>{t('drafts.publishing.lesson_success_modal.body')}</p>
        <div className="d-f ai-fs mb-24">
          <span className="c-gray-darker mr-4">
            <b>{t('common.note_with_colon')}</b>
          </span>
          <span className="c-gray-dark">
            {t('drafts.publishing.lesson_success_modal.note')}
          </span>
        </div>
        <label className="mb-16" htmlFor="toggle_display_message_preference">
          <input
            id="toggle_display_message_preference"
            type="checkbox"
            onChange={toggleDisplayMessagePreference}
            defaultChecked={false}
          />
          <span className="ml-4">{t('common.do_not_show_again')}</span>
        </label>
      </div>
      <ButtonGroup alignEnd>
        <Button onClick={toggleSuccessMessageVisible}>
          {t('common.dismiss')}
        </Button>
        {canAssign && (
          <Button primary onClick={handleAssign}>
            {t('drafts.publishing.lesson_success_modal.assign_lesson')}
          </Button>
        )}
      </ButtonGroup>
    </Modal>
  );
};

// react_on_rails workaround to get hooks to work
export default props => <DraftPublishSuccess {...props} />;
